// TanStack Query keys + fetchers for the broker surface (SM-621). Own
// file for the same reason wallet-queries.ts and currency-queries.ts
// are: the surface is self-contained and its cache keys should be
// invalidatable without touching anything else.
//
// Everything goes through backoffice-service's `/admin/broker-receivables*`
// and `/admin/brokers*` proxies, which gate on the BROKER_* permissions
// and forward to broker-service verbatim. The rules — which transition a
// receivable allows, who may approve what — are broker-service's; a
// refusal comes back as an APIError carrying its code (deciding an action
// that is no longer pending is 409 BROKER_APPROVAL_INVALID; the requester
// may approve their own settle or write-off, SM-946).

import { APIError, api, apiWithHeaders, qs } from "./api";
import { iconFormData } from "./queries";
import type { PageInfo } from "./types";
import type {
  AccountScope,
  BrokerAccountListResponse,
  BrokerCurrencyImageResponse,
  BrokerAuditListResponse,
  BrokerIntegrationItem,
  BrokerIntegrationListResponse,
  BrokerItem,
  BrokerListResponse,
  BrokerSummary,
  CreateBrokerRequest,
  CreateFeePlanRequest,
  CreateRevenueSharePlanRequest,
  EffectiveBrokerItem,
  ExposureLimitChangeRequest,
  ExposureLimitChangeResponse,
  ExposureLimitResponse,
  ExposureListResponse,
  ExposureView,
  FeeAssignmentItem,
  FeeAssignmentListResponse,
  FeePlanItem,
  FeePlanListResponse,
  FeePlanVersionItem,
  FeeResolution,
  FundingPoolListResponse,
  FundingReconRunItem,
  FundingTransferListResponse,
  InvestigationSubjectKind,
  InvestigationTrace,
  InviteStaffRequest,
  LifecycleTransition,
  LifecycleTransitionRequest,
  RevenueReconRunItem,
  RevenueReconRunListResponse,
  StaffCommand,
  StaffMembershipItem,
  StaffMembershipListResponse,
  StaffRole,
  PayoutBatchAction,
  PayoutBatchActionRequest,
  PayoutBatchDetail,
  PayoutBatchItem,
  PayoutBatchListResponse,
  LaunchAuditEvent,
  LaunchAuditListResponse,
  ProviderConfigEventListResponse,
  ProviderConfigResponse,
  ProviderLaunchListResponse,
  ProviderConfigUpsertRequest,
  ProviderCredentialCheckResponse,
  ProviderCredentialListResponse,
  ProviderCredentialRotateRequest,
  ProviderCredentialUploadRequest,
  ProviderCredentialUploadResponse,
  ProviderCredentialUploadStageRequest,
  ProviderCredentialUploadStageResponse,
  ProviderEnablementRequest,
  PublishFeeAssignmentRequest,
  PublishFeePlanVersionRequest,
  PublishRevenueShareAssignmentRequest,
  PublishRevenueShareVersionRequest,
  ReceivableItem,
  ReceivableListResponse,
  RecoveryAction,
  RecoveryActionRequest,
  RecoveryActionResponse,
  RecoveryCaseItem,
  RecoveryCaseListResponse,
  RecordSettlementNoteRequest,
  RetireFeePlanRequest,
  RetireRevenueSharePlanRequest,
  RevenueShareAssignmentItem,
  RevenueShareAssignmentListResponse,
  RevenueSharePlanDetail,
  RevenueSharePlanItem,
  RevenueSharePlanListResponse,
  RevenueSharePlanVersionItem,
  SettlementNoteItem,
  SettlementNoteListResponse,
  SettlementReferenceMatchesResponse,
  ConfirmSettlementNoteResponse,
  CreatePaymentRequestRequest,
  CreatePayoutBatchRequest,
  PaymentRequestItem,
  PaymentRequestListResponse,
  SettlementPositionListResponse,
  SettlementPositionResponse,
  StaffFeeScheduleResponse,
} from "./broker-types";

export interface ReceivableFilter {
  /** UNRESOLVED, one ReceivableState, or undefined for every state. */
  state?: string;
  broker_id?: string;
  currency?: string;
  limit: number;
  offset: number;
}

export interface PayoutBatchFilter {
  /** One PayoutBatchState, or undefined for every state. */
  state?: string;
  broker_id?: string;
  currency?: string;
  limit: number;
  offset: number;
}

/** The directory's filters (GET /admin/brokers). */
export interface BrokerListFilter {
  /** Name (display / legal) or exact broker id. */
  q?: string;
  /** One status; absent = every status but CLOSED unless include_closed. */
  status?: string;
  roots_only?: boolean;
  include_closed?: boolean;
  parent_broker_id?: string;
  root_broker_id?: string;
  limit: number;
  offset: number;
}

export interface BrokerPageFilter {
  limit: number;
  offset: number;
}

/** GET /admin/provider-recovery/cases. Absent = every value. */
export interface RecoveryCaseFilter extends BrokerPageFilter {
  state?: string;
  severity?: string;
  kind?: string;
  broker_account_id?: string;
}

export interface BrokerStaffFilter extends BrokerPageFilter {
  include_removed?: boolean;
}

/** GET /admin/brokers/{id}/accounts. */
export interface BrokerAccountsFilter extends BrokerPageFilter {
  scope: AccountScope;
  status?: string;
  balance_mode?: string;
  /** Prefix of a user id or broker account id. */
  q?: string;
}

export interface BrokerExposureListFilter extends BrokerPageFilter {
  currency: string;
}

/** One currency and a period [from, to) in unix ms — both bounds required. */
export interface SettlementWindow {
  currency: string;
  from_unix_ms: number;
  to_unix_ms: number;
  scope?: "node" | "subtree";
}

export interface SettlementListFilter extends SettlementWindow, BrokerPageFilter {
  direction?: string;
  root_broker_id?: string;
  roots_only?: boolean;
  /** The direct children of this node (SM-884); never with roots_only. */
  parent_broker_id?: string;
  sort?: string;
}

/** GET /admin/brokers/{id}/payment-requests. */
export interface BrokerPaymentRequestFilter extends BrokerPageFilter {
  scope?: "node" | "subtree";
  status?: string;
}

/** GET /admin/payment-requests — every node's requests. */
export interface PaymentRequestIndexFilter extends BrokerPageFilter {
  status?: string;
  broker_id?: string;
  root_broker_id?: string;
}

export interface SettlementNotesFilter extends BrokerPageFilter {
  currency?: string;
  status?: string;
  scope?: "node" | "subtree";
}

export const brokerKeys = {
  all: ["brokers"] as const,
  receivables: (f: ReceivableFilter) => [...brokerKeys.all, "receivables", f] as const,
  receivable: (id: string) => [...brokerKeys.all, "receivable", id] as const,
  broker: (id: string) => [...brokerKeys.all, "broker", id] as const,
  payoutBatches: (f: PayoutBatchFilter) => [...brokerKeys.all, "payout-batches", f] as const,
  payoutBatch: (id: string, f: BrokerPageFilter) => [...brokerKeys.all, "payout-batch", id, f] as const,
  // governance (SM-716)
  list: (f: BrokerListFilter) => [...brokerKeys.all, "list", f] as const,
  path: (id: string) => [...brokerKeys.all, "path", id] as const,
  descendants: (id: string) => [...brokerKeys.all, "descendants", id] as const,
  audit: (id: string, f: BrokerPageFilter) => [...brokerKeys.all, "audit", id, f] as const,
  // SM-984: a broker's GOLD currency image
  currencyImage: (id: string) => [...brokerKeys.all, "currency-image", id] as const,
  staff: (id: string, f: BrokerStaffFilter) => [...brokerKeys.all, "staff", id, f] as const,
  accounts: (id: string, f: BrokerAccountsFilter) => [...brokerKeys.all, "accounts", id, f] as const,
  pools: (id: string) => [...brokerKeys.all, "pools", id] as const,
  transfers: (id: string) => [...brokerKeys.all, "transfers", id] as const,
  exposure: (id: string, currency: string) => [...brokerKeys.all, "exposure", id, currency] as const,
  exposureLimit: (id: string, currency: string) => [...brokerKeys.all, "exposure-limit", id, currency] as const,
  exposureList: (f: BrokerExposureListFilter) => [...brokerKeys.all, "exposure-list", f] as const,
  integration: (id: string) => [...brokerKeys.all, "integration", id] as const,
  integrations: (f: BrokerPageFilter) => [...brokerKeys.all, "integrations", f] as const,
  fundingRecon: () => [...brokerKeys.all, "funding-recon"] as const,
  revenueReconRuns: (f: BrokerPageFilter) => [...brokerKeys.all, "revenue-recon-runs", f] as const,
  revenueReconRun: (id: string) => [...brokerKeys.all, "revenue-recon-run", id] as const,
  investigation: (kind: string, id: string) => [...brokerKeys.all, "investigation", kind, id] as const,
  // settlement (SM-796 / SM-798 / SM-799)
  settlementPositions: (f: SettlementListFilter) => [...brokerKeys.all, "settlement-positions", f] as const,
  settlementPosition: (id: string, f: SettlementWindow) => [...brokerKeys.all, "settlement-position", id, f] as const,
  settlementNotes: (id: string, f: SettlementNotesFilter) => [...brokerKeys.all, "settlement-notes", id, f] as const,
  settlementReferences: (reference: string, excludeNoteID: string) =>
    [...brokerKeys.all, "settlement-references", reference, excludeNoteID] as const,
  // payment requests (SM-878 S4)
  paymentRequests: (id: string, f: BrokerPaymentRequestFilter) => [...brokerKeys.all, "payment-requests", id, f] as const,
  paymentRequestIndex: (f: PaymentRequestIndexFilter) => [...brokerKeys.all, "payment-request-index", f] as const,
  // external-credit provider configuration (SM-634)
  providerConfig: (id: string) => [...brokerKeys.all, "provider-config", id] as const,
  providerLaunch: (ids: readonly string[]) => [...brokerKeys.all, "provider-launch", ids] as const,
  providerEvents: (id: string, f: BrokerPageFilter) => [...brokerKeys.all, "provider-events", id, f] as const,
  providerCredentials: (id: string) => [...brokerKeys.all, "provider-credentials", id] as const,
  launchAudit: (id: string, f: BrokerPageFilter) => [...brokerKeys.all, "launch-audit", id, f] as const,
  // provider-operation recovery cases (SM-661)
  recoveryCases: (f: RecoveryCaseFilter) => [...brokerKeys.all, "recovery-cases", f] as const,
  recoveryCase: (id: string) => [...brokerKeys.all, "recovery-case", id] as const,
  // fee plans, assignments and the effective schedule (SM-839 / SM-840)
  feePlans: (rootID: string, f: BrokerPageFilter) => [...brokerKeys.all, "fee-plans", rootID, f] as const,
  feePlan: (rootID: string, planID: string) => [...brokerKeys.all, "fee-plan", rootID, planID] as const,
  feeAssignments: (rootID: string, f: BrokerPageFilter) => [...brokerKeys.all, "fee-assignments", rootID, f] as const,
  feeSchedule: (id: string, currency: string) => [...brokerKeys.all, "fee-schedule", id, currency] as const,
  feeResolution: (accountID: string, currency: string, atUnixMS: number) =>
    [...brokerKeys.all, "fee-resolution", accountID, currency, atUnixMS] as const,
  // revenue-share plans and assignments (SM-841)
  sharePlans: (rootID: string, f: BrokerPageFilter) => [...brokerKeys.all, "share-plans", rootID, f] as const,
  sharePlan: (rootID: string, planID: string) => [...brokerKeys.all, "share-plan", rootID, planID] as const,
  shareAssignments: (rootID: string, f: BrokerPageFilter) => [...brokerKeys.all, "share-assignments", rootID, f] as const,
};

const EMPTY_PAGE: PageInfo = { total: 0, limit: 0, offset: 0, has_more: false };

export const fetchBrokerReceivables = (f: ReceivableFilter) =>
  api<ReceivableListResponse>(
    `/admin/broker-receivables${qs({
      state: f.state,
      broker_id: f.broker_id,
      currency: f.currency,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<ReceivableListResponse>((r) => ({
    items: r?.items ?? [],
    page: r?.page ?? { ...EMPTY_PAGE, limit: f.limit, offset: f.offset },
  }));

export const fetchBrokerReceivable = (id: string) =>
  api<ReceivableItem>(`/admin/broker-receivables/${encodeURIComponent(id)}`);

/** The node's names, for labelling a receivable's root. Needs BROKER_VIEW. */
export const fetchBrokerSummary = (id: string) =>
  api<BrokerSummary>(`/admin/brokers/${encodeURIComponent(id)}`);

const receivablePath = (id: string) => `/admin/broker-receivables/${encodeURIComponent(id)}`;

/** Takes ownership: OPEN → ACKNOWLEDGED, the acting admin becomes the owner. */
export const acknowledgeBrokerReceivable = (id: string, reason: string) =>
  api<ReceivableItem>(`${receivablePath(id)}/acknowledge`, {
    method: "POST",
    body: { reason },
  });

/**
 * What a settle names (SM-948): a confirmed INBOUND settlement note of
 * the root — the server then takes the reference from it — or, for a
 * payment with no note, the external payment reference. Never both.
 */
export type ReceivableSettleSource = { settlementNoteID: string } | { reference: string };

/**
 * Records that the broker paid out of band. Creates a PENDING action; the
 * receivable resolves when an admin approves it (the requester included,
 * SM-946).
 */
export const settleBrokerReceivable = (id: string, source: ReceivableSettleSource, reason: string) =>
  api<ReceivableItem>(`${receivablePath(id)}/settle`, {
    method: "POST",
    body:
      "settlementNoteID" in source
        ? { settlement_note_id: source.settlementNoteID, reason }
        : { reference: source.reference, reason },
  });

/** Same maker-checker shape as settle; the resolution is a loss. */
export const writeOffBrokerReceivable = (id: string, reason: string) =>
  api<ReceivableItem>(`${receivablePath(id)}/write-off`, {
    method: "POST",
    body: { reason },
  });

export const decideBrokerReceivableAction = (
  id: string,
  actionID: string,
  decision: "approve" | "reject",
  reason: string,
) =>
  api<ReceivableItem>(
    `${receivablePath(id)}/actions/${encodeURIComponent(actionID)}/${decision}`,
    { method: "POST", body: { reason } },
  );

// ── payout batches (SM-710) ────────────────────────────────────────────
//
// /admin/payout-batches* proxies (BROKER_REVENUE_VIEW to read,
// BROKER_PAYOUT_REVIEW to act). broker-service owns the state machine
// and who may decide: a reject by the admin who prepared the batch, or a
// second decision by the same admin, is 409 BROKER_APPROVAL_INVALID (the
// preparer may approve, SM-914); an action the batch's state does not
// allow is 409 BROKER_PAYOUT_BATCH_TRANSITION_INVALID.

export const fetchBrokerPayoutBatches = (f: PayoutBatchFilter) =>
  api<PayoutBatchListResponse>(
    `/admin/payout-batches${qs({
      state: f.state,
      broker_id: f.broker_id,
      currency: f.currency,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<PayoutBatchListResponse>((r) => ({
    items: r?.items ?? [],
    page: r?.page ?? { ...EMPTY_PAGE, limit: f.limit, offset: f.offset },
  }));

/**
 * One batch, a page of its supporting earnings (`limit`/`offset` page the
 * earnings only — without them broker-service returns the first 10) and
 * its whole history.
 */
export const fetchBrokerPayoutBatch = (id: string, f: BrokerPageFilter) =>
  api<PayoutBatchDetail>(
    `/admin/payout-batches/${encodeURIComponent(id)}${qs({ limit: f.limit, offset: f.offset })}`,
  ).then<PayoutBatchDetail>((r) => ({
    ...r,
    items: r?.items ?? [],
    events: r?.events ?? [],
    page: r?.page ?? { ...EMPTY_PAGE, limit: f.limit, offset: f.offset },
  }));

/** One review / execution step. Every step carries the reviewer's reason. */
export const actOnBrokerPayoutBatch = (id: string, action: PayoutBatchAction, body: PayoutBatchActionRequest) =>
  api<PayoutBatchItem>(`/admin/payout-batches/${encodeURIComponent(id)}/${action}`, {
    method: "POST",
    body,
  });

// ── broker governance (SM-716, SM-621 phase 08a) ─────────────────────
//
// /admin/brokers* proxies: reads BROKER_VIEW (exposure BROKER_EXPOSURE_VIEW,
// audit BROKER_AUDIT_VIEW, the investigation BROKER_INVESTIGATE); each
// lifecycle verb its own code (approve BROKER_APPROVE; pause, resume and
// the emergency disable BROKER_PAUSE; begin-close and close
// BROKER_CLOSE); staff changes BROKER_STAFF_MANAGE. broker-service owns
// every rule — which transition a status allows, what blocks a closure
// (409 BROKER_CLOSE_BLOCKED with `blocking[]`), the last-owner and
// escalation guards on staff — and a refusal comes back as an APIError
// carrying its code and the broker error body.
//
// Every successful broker-service mutation names the audit events it
// wrote in X-Broker-Audit-Event-Ids (D65); the mutations below return
// them next to the body so the confirm dialog can show the reference.

/** broker-service's audit reference header (contracts HeaderBrokerAuditEventIDs). */
export const HEADER_BROKER_AUDIT_EVENT_IDS = "X-Broker-Audit-Event-Ids";

/** The audit event ids a response header names, in write order. */
export function auditEventIDs(headers: Headers | undefined | null): string[] {
  const raw = headers?.get(HEADER_BROKER_AUDIT_EVENT_IDS) ?? "";
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s !== "");
}

/** A mutation's answer with the audit events it wrote. */
export interface AuditedResult<T> {
  data: T;
  auditEventIds: string[];
}

/** A mutation's answer with its audit events; idempotencyKey rides as the Idempotency-Key header when set. */
async function audited<T>(
  path: string,
  body: unknown,
  method: "POST" | "PUT" | "DELETE" = "POST",
  idempotencyKey?: string,
): Promise<AuditedResult<T>> {
  const res = await apiWithHeaders<T>(path, { method, body, idempotencyKey });
  return { data: res.data, auditEventIds: auditEventIDs(res.headers) };
}

const brokerPath = (id: string) => `/admin/brokers/${encodeURIComponent(id)}`;

const pageOf = <T>(r: { items?: T[]; page?: PageInfo } | null | undefined, f: BrokerPageFilter) => ({
  items: r?.items ?? [],
  page: r?.page ?? { ...EMPTY_PAGE, limit: f.limit, offset: f.offset },
});

export const fetchBrokers = (f: BrokerListFilter) =>
  api<BrokerListResponse>(
    `/admin/brokers${qs({
      q: f.q,
      status: f.status,
      roots_only: f.roots_only ? "true" : undefined,
      include_closed: f.include_closed ? "true" : undefined,
      parent_broker_id: f.parent_broker_id,
      root_broker_id: f.root_broker_id,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<BrokerListResponse>((r) => pageOf(r, f));

/** One node with its effective status (the same read as fetchBrokerSummary). */
export const fetchBroker = (id: string) => api<BrokerItem>(brokerPath(id));

/** SM-984: GET /admin/brokers/{id}/currency-image (BROKER_VIEW). */
export const fetchBrokerCurrencyImage = (id: string) =>
  api<BrokerCurrencyImageResponse>(`${brokerPath(id)}/currency-image`);

/** SM-987: POST /admin/brokers/{id}/currency-image (BROKER_SETTINGS_UPDATE), multipart field "file"; live at once. */
export const uploadBrokerCurrencyImage = (id: string, file: File) =>
  audited<BrokerCurrencyImageResponse>(`${brokerPath(id)}/currency-image`, iconFormData(file));

/** SM-984: DELETE /admin/brokers/{id}/currency-image (BROKER_SETTINGS_UPDATE); idempotent. */
export const removeBrokerCurrencyImage = (id: string) =>
  audited<BrokerCurrencyImageResponse>(`${brokerPath(id)}/currency-image`, undefined, "DELETE");

/** The node's resolved root→node path and the node restricting it. */
export const fetchBrokerPath = (id: string) =>
  api<EffectiveBrokerItem>(`${brokerPath(id)}/path`).then<EffectiveBrokerItem>((r) => ({ ...r, path: r?.path ?? [] }));

/** Every descendant, nearest first ((distance, broker_id) order), with effective status. */
export const fetchBrokerDescendants = (id: string, f: BrokerPageFilter) =>
  api<BrokerListResponse>(`${brokerPath(id)}/descendants${qs({ limit: f.limit, offset: f.offset })}`).then<BrokerListResponse>(
    (r) => pageOf(r, f),
  );

export const fetchBrokerAudit = (id: string, f: BrokerPageFilter) =>
  api<BrokerAuditListResponse>(`${brokerPath(id)}/audit${qs({ limit: f.limit, offset: f.offset })}`).then<BrokerAuditListResponse>(
    (r) => pageOf(r, f),
  );

export const fetchBrokerStaff = (id: string, f: BrokerStaffFilter) =>
  api<StaffMembershipListResponse>(
    `${brokerPath(id)}/staff${qs({
      include_removed: f.include_removed ? "true" : undefined,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<StaffMembershipListResponse>((r) => pageOf(r, f));

export const fetchBrokerAccounts = (id: string, f: BrokerAccountsFilter) =>
  api<BrokerAccountListResponse>(
    `${brokerPath(id)}/accounts${qs({
      scope: f.scope,
      status: f.status,
      balance_mode: f.balance_mode,
      q: f.q,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<BrokerAccountListResponse>((r) => pageOf(r, f));

/** Every currency the node holds, one row each (GOLD, RED, GREEN order). */
export const fetchBrokerFundingPools = (id: string) =>
  api<FundingPoolListResponse>(`${brokerPath(id)}/funding/pools`).then<FundingPoolListResponse>((r) => ({
    ...r,
    pools: r?.pools ?? [],
  }));

/** Transfers the node is party to, newest first, cursor-paged. */
export const fetchBrokerFundingTransfers = (id: string, cursor: string | undefined, limit: number) =>
  api<FundingTransferListResponse>(`${brokerPath(id)}/funding/transfers${qs({ cursor, limit })}`).then<FundingTransferListResponse>(
    (r) => ({ items: r?.items ?? [], next_cursor: r?.next_cursor || undefined }),
  );

export const fetchBrokerExposure = (id: string, currency: string) =>
  api<ExposureView>(`${brokerPath(id)}/exposure${qs({ currency })}`);

export const fetchBrokerExposureLimit = (id: string, currency: string) =>
  api<ExposureLimitResponse>(`${brokerPath(id)}/exposure-limit${qs({ currency })}`).then<ExposureLimitResponse>((r) => ({
    ...r,
    pending: r?.pending ?? [],
  }));

// Limit changes (SM-945): BROKER_EXPOSURE_CONFIGURE. broker-service
// applies a risk-reducing change at once and stores a raise as a PENDING
// request (`applied: false`, `request` set) that a different admin
// approves; only the requester cancels, any admin rejects.

export const changeBrokerExposureLimit = (id: string, body: ExposureLimitChangeRequest) =>
  api<ExposureLimitChangeResponse>(`${brokerPath(id)}/exposure-limit`, { method: "PUT", body });

export const decideBrokerExposureLimitRequest = (
  id: string,
  requestID: string,
  decision: "approve" | "reject" | "cancel",
  reason: string,
) =>
  api<ExposureLimitChangeResponse>(
    `${brokerPath(id)}/exposure-limit/requests/${encodeURIComponent(requestID)}/${decision}`,
    { method: "POST", body: { reason } },
  );

/** Every non-DRAFT, non-CLOSED node's exposure in one currency. */
export const fetchBrokerExposureList = (f: BrokerExposureListFilter) =>
  api<ExposureListResponse>(
    `/admin/brokers/exposure${qs({ currency: f.currency, limit: f.limit, offset: f.offset })}`,
  ).then<ExposureListResponse>((r) => pageOf(r, f));

export const fetchBrokerIntegration = (id: string) => api<BrokerIntegrationItem>(`${brokerPath(id)}/integration`);

export const fetchBrokerIntegrations = (f: BrokerPageFilter) =>
  api<BrokerIntegrationListResponse>(
    `/admin/brokers/integrations${qs({ limit: f.limit, offset: f.offset })}`,
  ).then<BrokerIntegrationListResponse>((r) => pageOf(r, f));

/** The latest completed funding reconciliation run; null before the first one. */
export async function fetchFundingReconLatest(): Promise<FundingReconRunItem | null> {
  try {
    const r = await api<FundingReconRunItem>("/admin/funding/reconciliation/latest");
    return r ? { ...r, roots: r.roots ?? [], findings: r.findings ?? [] } : null;
  } catch (e) {
    // broker-service answers 404 until the first run has completed.
    if (e instanceof APIError && e.status === 404) return null;
    throw e;
  }
}

export const fetchRevenueReconRuns = (f: BrokerPageFilter) =>
  api<RevenueReconRunListResponse>(
    `/admin/revenue/reconciliation/runs${qs({ limit: f.limit, offset: f.offset })}`,
  ).then<RevenueReconRunListResponse>((r) => pageOf(r, f));

export const fetchRevenueReconRun = (runID: string) =>
  api<RevenueReconRunItem>(`/admin/revenue/reconciliation/runs/${encodeURIComponent(runID)}`).then<RevenueReconRunItem>(
    (r) => ({ ...r, items: r?.items ?? [], escrow: r?.escrow ?? [] }),
  );

/** The query parameter each investigation subject is asked by (exactly one). */
export const INVESTIGATION_PARAM: Record<InvestigationSubjectKind, string> = {
  USER: "user_id",
  BROKER_ACCOUNT: "broker_account_id",
  ORDER: "order_id",
  TRADE: "trade_id",
  PAYOUT_BATCH: "payout_batch_id",
};

export const fetchBrokerInvestigation = (kind: InvestigationSubjectKind, id: string) =>
  api<InvestigationTrace>(`/admin/brokers/investigate${qs({ [INVESTIGATION_PARAM[kind]]: id })}`).then<InvestigationTrace>(
    (r) => ({ ...r, accounts: r?.accounts ?? [], steps: r?.steps ?? [], gaps: r?.gaps ?? [] }),
  );

/**
 * One lifecycle verb. The body carries the reason (mandatory), the row
 * version the operator decided on — so a retry after a lost response is
 * refused BROKER_LIFECYCLE_CONFLICT instead of applying twice — and, for
 * the emergency disable, reason_kind EMERGENCY on a pause.
 */
export const transitionBroker = (id: string, transition: LifecycleTransition, body: LifecycleTransitionRequest) =>
  audited<BrokerItem>(`${brokerPath(id)}/${transition}`, body);

/**
 * Create a broker in DRAFT (SM-963, BROKER_CREATE): a new organisation
 * (no parent_broker_id) or a sub-broker. idempotencyKey is the Create
 * broker dialog's one key per opening (SM-961): a retry after a lost
 * answer gets the first broker back (200, no audit reference) instead of
 * a second draft.
 */
export const createBroker = (body: CreateBrokerRequest, idempotencyKey: string) =>
  audited<BrokerItem>("/admin/brokers", body, "POST", idempotencyKey);

export const inviteBrokerStaff = (id: string, body: InviteStaffRequest) =>
  audited<StaffMembershipItem>(`${brokerPath(id)}/staff`, body);

export const commandBrokerStaff = (id: string, membershipID: string, command: StaffCommand, reason: string) =>
  audited<StaffMembershipItem>(`${brokerPath(id)}/staff/${encodeURIComponent(membershipID)}/${command}`, { reason });

export const changeBrokerStaffRole = (id: string, membershipID: string, role: StaffRole, reason: string) =>
  audited<StaffMembershipItem>(`${brokerPath(id)}/staff/${encodeURIComponent(membershipID)}/role`, { role, reason });

/** A fresh set-password link for an invitee who has not set a password (SM-968); the old link dies. */
export const resendBrokerStaffInvite = (id: string, membershipID: string, reason: string) =>
  audited<StaffMembershipItem>(`${brokerPath(id)}/staff/${encodeURIComponent(membershipID)}/resend-invite`, { reason });

// --- settlement position and notes (SM-621 phase 09a) ---
//
// broker-service computes every figure (backoffice relays it byte for
// byte); the console renders. A note is an inert memo — recording or
// confirming one moves no money anywhere.

const settlementQuery = (f: SettlementWindow & Partial<SettlementListFilter>) =>
  qs({
    currency: f.currency,
    from_unix_ms: f.from_unix_ms,
    to_unix_ms: f.to_unix_ms,
    scope: f.scope,
    direction: f.direction,
    root_broker_id: f.root_broker_id,
    roots_only: f.roots_only ? "true" : undefined,
    parent_broker_id: f.parent_broker_id,
    sort: f.sort,
    limit: f.limit,
    offset: f.offset,
  });

export const fetchSettlementPositions = (f: SettlementListFilter) =>
  api<SettlementPositionListResponse>(`/admin/brokers/settlement-positions${settlementQuery(f)}`).then((r) => ({
    ...r,
    items: r.items ?? [],
    page: r.page ?? EMPTY_PAGE,
  }));

/**
 * CSV export URL — a plain link (the browser handles the download and its
 * Content-Disposition); the same filters as the list on screen, every row,
 * one currency.
 */
export const settlementExportURL = (f: SettlementListFilter) =>
  `/admin/brokers/settlement-positions.csv${settlementQuery({ ...f, limit: undefined, offset: undefined })}`;

export const fetchSettlementPosition = (id: string, f: SettlementWindow) =>
  api<SettlementPositionResponse>(
    `${brokerPath(id)}/settlement-position${qs({ currency: f.currency, from_unix_ms: f.from_unix_ms, to_unix_ms: f.to_unix_ms, scope: f.scope })}`,
  );

export const fetchSettlementNotes = (id: string, f: SettlementNotesFilter) =>
  api<SettlementNoteListResponse>(
    `${brokerPath(id)}/settlement-notes${qs({ currency: f.currency, status: f.status, scope: f.scope, limit: f.limit, offset: f.offset })}`,
  ).then((r) => ({ ...r, items: r.items ?? [], page: r.page ?? EMPTY_PAGE }));

/**
 * Live notes and receivable settles that already use a bank reference
 * (SM-948): a warning only, never a refusal. excludeNoteID drops the note
 * being corrected and any settle linked to it. Needs
 * BROKER_SETTLEMENT_NOTE_RECORD or BROKER_RECEIVABLE_MANAGE.
 */
export const fetchSettlementReferenceMatches = (reference: string, excludeNoteID?: string) =>
  api<SettlementReferenceMatchesResponse>(
    `/admin/brokers/settlement-references${qs({ reference, exclude_note_id: excludeNoteID })}`,
  ).then((r) => ({ notes: r?.notes ?? [], receivable_settles: r?.receivable_settles ?? [] }));

/**
 * idempotencyKey is the Record dialog's one key per opening (SM-944): a
 * repeat with the same key answers the first note instead of recording a
 * second; a different body under it is 409 BROKER_IDEMPOTENCY_CONFLICT.
 */
export const recordSettlementNote = (id: string, body: RecordSettlementNoteRequest, idempotencyKey?: string) =>
  api<SettlementNoteItem>(`${brokerPath(id)}/settlement-notes`, { method: "POST", body, idempotencyKey });

// --- payment requests (SM-878 S4 / SM-883) ---
//
// Reads BROKER_SETTLEMENT_VIEW; create and cancel
// BROKER_SETTLEMENT_NOTE_RECORD. A request records that the broker was
// asked for money; it moves none.

export const fetchBrokerPaymentRequests = (id: string, f: BrokerPaymentRequestFilter) =>
  api<PaymentRequestListResponse>(
    `${brokerPath(id)}/payment-requests${qs({ scope: f.scope, status: f.status, limit: f.limit, offset: f.offset })}`,
  ).then((r) => pageOf(r, f));

export const fetchPaymentRequests = (f: PaymentRequestIndexFilter) =>
  api<PaymentRequestListResponse>(
    `/admin/payment-requests${qs({
      status: f.status,
      broker_id: f.broker_id,
      root_broker_id: f.root_broker_id,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then((r) => pageOf(r, f));

/**
 * idempotencyKey is the Request payment dialog's one key per opening
 * (SM-912): a repeat with the same key answers the first request instead
 * of recording a second; a different body under it is 409
 * BROKER_IDEMPOTENCY_CONFLICT.
 */
export const createPaymentRequest = (id: string, body: CreatePaymentRequestRequest, idempotencyKey?: string) =>
  api<PaymentRequestItem>(`${brokerPath(id)}/payment-requests`, { method: "POST", body, idempotencyKey });

export const cancelPaymentRequest = (requestID: string, reason: string) =>
  api<PaymentRequestItem>(`/admin/payment-requests/${encodeURIComponent(requestID)}/cancel`, {
    method: "POST",
    body: { reason },
  });

/**
 * An operator's manual payout batch for one node (BROKER_PAYOUT_REVIEW).
 * Every batch is prepared by hand (SM-878); an admin approves it, its
 * preparer included (SM-914).
 */
export const createBrokerPayoutBatch = (id: string, body: CreatePayoutBatchRequest) =>
  api<PayoutBatchItem>(`${brokerPath(id)}/payout-batches`, { method: "POST", body });

export const confirmSettlementNote = (noteID: string, reason: string) =>
  api<ConfirmSettlementNoteResponse>(`/admin/brokers/settlement-notes/${encodeURIComponent(noteID)}/confirm`, {
    method: "POST",
    body: reason ? { reason } : {},
  });

// --- external-credit provider configuration (SM-621 phase 11, SM-634) ---
//
// Reads BROKER_PROVIDER_VIEW; the upsert, enable / disable and the
// credential rotation drill BROKER_PROVIDER_CONFIGURE. Every shape is
// redacted by construction (the credential is a Secret Manager
// reference); broker-service owns validation and the fail-closed
// enablement, and its refusals come back as an APIError (an enable
// refused is 409 BROKER_PROVIDER_ENABLE_REFUSED naming every unmet rule).

const providerConfigPath = (id: string) => `${brokerPath(id)}/provider-config`;

export const fetchProviderConfig = (id: string) =>
  api<ProviderConfigResponse>(providerConfigPath(id)).then<ProviderConfigResponse>((r) => ({
    ...r,
    enablement: { ready: r?.enablement?.ready ?? false, blockers: r?.enablement?.blockers ?? [] },
    // A broker-service from before SM-919 sends no credential_upload: no upload.
    credential_upload: {
      available: r?.credential_upload?.available === true,
      secret_id_prefix: r?.credential_upload?.secret_id_prefix ?? "",
    },
    // A broker-service from before SM-935 sends no defaults: nothing is prefilled.
    defaults: r?.defaults?.contract_version ? { ...r.defaults, reason_allowlist: r.defaults.reason_allowlist ?? [] } : undefined,
  }));

export const fetchProviderEvents = (id: string, f: BrokerPageFilter) =>
  api<ProviderConfigEventListResponse>(`${providerConfigPath(id)}/events${qs({ limit: f.limit, offset: f.offset })}`).then<
    ProviderConfigEventListResponse
  >((r) => pageOf(r, f));

export const saveProviderConfig = (id: string, body: ProviderConfigUpsertRequest) =>
  audited<ProviderConfigResponse>(providerConfigPath(id), body, "PUT");

export const setProviderEnablement = (id: string, body: ProviderEnablementRequest) =>
  audited<ProviderConfigResponse>(`${providerConfigPath(id)}/enablement`, body);

/** The rotation drill: re-resolve the reference; the answer is metadata only. */
export const runProviderCredentialCheck = (id: string, reason: string) =>
  audited<ProviderCredentialCheckResponse>(`${providerConfigPath(id)}/credential-check`, { reason });

/**
 * SM-919 (BROKER_PROVIDER_SECRET_WRITE): store a pasted secret key in
 * Secret Manager; the 201 is metadata only, with credential_ref pinned to
 * the stored version for the operator's next save. The upload changes no
 * configuration.
 *
 * The body carries the value, so this is a plain call — never a
 * useMutation, whose variables the MutationCache keeps — and the caller
 * holds the value in local state only. api() puts the RESPONSE body on a
 * thrown APIError, never the request body, and logs nothing.
 */
export const uploadProviderCredential = (id: string, body: ProviderCredentialUploadRequest) =>
  api<ProviderCredentialUploadResponse>(`${providerConfigPath(id)}/credential-upload`, { method: "POST", body });

// ── provider key versions (SM-926) ──

const providerCredentialsPath = (id: string) => `${brokerPath(id)}/provider-credentials`;

const credentialList = <T extends ProviderCredentialListResponse>(r: T): T => ({
  ...r,
  active_signing_version: r?.active_signing_version ?? null,
  rotation_due_at: r?.rotation_due_at ?? null,
  versions: r?.versions ?? [],
  events: r?.events ?? [],
});

/** A broker's provider key versions and their events, newest first (BROKER_PROVIDER_VIEW). */
export const fetchProviderCredentials = (id: string) =>
  api<ProviderCredentialListResponse>(providerCredentialsPath(id)).then(credentialList);

const auditedList = (path: string, body: unknown) =>
  audited<ProviderCredentialListResponse>(path, body).then((r) => ({ ...r, data: credentialList(r.data) }));

/** Stage an existing Secret Manager version as a new key (CONFIGURE). */
export const stageProviderCredential = (id: string, body: ProviderCredentialRotateRequest) =>
  auditedList(`${providerCredentialsPath(id)}/rotate`, body);

/** Start using a staged key; the key in use stays accepted for 7 days (CONFIGURE). */
export const activateProviderCredential = (id: string, keyVersion: number, reason: string) =>
  auditedList(`${providerCredentialsPath(id)}/${keyVersion}/activate`, { reason });

/** Go back to the newest earlier key that is not blocked; the server decides which (CONFIGURE). */
export const rollbackProviderCredential = (id: string, reason: string) =>
  auditedList(`${providerCredentialsPath(id)}/rollback`, { reason });

/** Block a key at once, no overlap (CONFIGURE). Blocking the key in use stops every provider call. */
export const revokeProviderCredential = (id: string, keyVersion: number, reason: string) =>
  auditedList(`${providerCredentialsPath(id)}/${keyVersion}/revoke`, { reason });

/**
 * SM-926 (BROKER_PROVIDER_SECRET_WRITE): store a pasted key and stage it,
 * while the provider stays enabled. The body carries the value, so — like
 * uploadProviderCredential — this is a plain call, never a useMutation,
 * and the caller holds the value in local state only.
 */
export const uploadStageProviderCredential = (id: string, body: ProviderCredentialUploadStageRequest) =>
  apiWithHeaders<ProviderCredentialUploadStageResponse>(`${providerCredentialsPath(id)}/upload`, { method: "POST", body }).then(
    (r) => ({ data: credentialList(r.data), auditEventIds: auditEventIDs(r.headers) }),
  );

/**
 * SM-903: the Integrations page's batched launch read for the page's
 * CONFIGURED ids (BROKER_PROVIDER_VIEW). Never called with no ids —
 * broker-service answers that 400, never "all brokers".
 */
export const fetchBrokerProviderLaunch = (ids: readonly string[]) =>
  api<ProviderLaunchListResponse>(`/admin/brokers/provider-launch${qs({ broker_ids: ids.join(",") })}`).then<
    ProviderLaunchListResponse
  >((r) => ({
    launch_active: r?.launch_active ?? false,
    launch_base_urls: r?.launch_base_urls ?? [],
    items: r?.items ?? [],
  }));

// --- provider launch audit (SM-621 phase 12, SM-643) ---
//
// Reads BROKER_LAUNCH_AUDIT_VIEW; read-only, there is no verb. The wire
// answers { items, total } (newest first) without a page envelope, so the
// page is rebuilt here for <Pagination>.

export const fetchLaunchAudit = (id: string, f: BrokerPageFilter) =>
  api<LaunchAuditListResponse>(`${brokerPath(id)}/launch-audit${qs({ limit: f.limit, offset: f.offset })}`).then<{
    items: LaunchAuditEvent[];
    page: PageInfo;
  }>((r) => {
    const items = r?.items ?? [];
    const total = r?.total ?? 0;
    return { items, page: { total, limit: f.limit, offset: f.offset, has_more: f.offset + items.length < total } };
  });

// --- provider-operation recovery cases (SM-621 phase 14, SM-661) ---
//
// /admin/provider-recovery/cases* proxies: reads BROKER_PROVIDER_CASE_VIEW,
// the six actions BROKER_PROVIDER_RECOVERY_ACT. An action's body is a note
// ONLY — backoffice and broker-service both refuse any other field (400),
// so no amount, balance or transaction id can ever be sent from here.
// broker-service re-runs the action's eligibility at the moment it
// arrives; a refusal is 409 RECOVERY_ACTION_NOT_ELIGIBLE with
// details.reason.

const recoveryCasePath = (id: string) => `/admin/provider-recovery/cases/${encodeURIComponent(id)}`;

export const fetchRecoveryCases = (f: RecoveryCaseFilter) =>
  api<RecoveryCaseListResponse>(
    `/admin/provider-recovery/cases${qs({
      state: f.state,
      severity: f.severity,
      kind: f.kind,
      broker_account_id: f.broker_account_id,
      limit: f.limit,
      offset: f.offset,
    })}`,
  ).then<RecoveryCaseListResponse>((r) => pageOf(r, f));

export const fetchRecoveryCase = (id: string) =>
  api<RecoveryCaseItem>(recoveryCasePath(id)).then<RecoveryCaseItem>((r) => ({
    ...r,
    eligible_actions: r?.eligible_actions ?? [],
    actions: r?.actions ?? [],
  }));

/** One audited operator action. The body is {note} and nothing else. */
export const takeRecoveryAction = (id: string, action: RecoveryAction, note: string) => {
  const trimmed = note.trim();
  const body: RecoveryActionRequest = trimmed ? { note: trimmed } : {};
  return audited<RecoveryActionResponse>(`${recoveryCasePath(id)}/actions/${encodeURIComponent(action)}`, body);
};

// --- broker fee plans, assignments and resolution (SM-839, SM-840) ---
//
// /admin/brokers/{root_id}/fee-plans* and /fee-assignments (backoffice
// SM-838: reads BROKER_FEE_VIEW, writes BROKER_FEE_MANAGE), the
// effective schedule of ANY node (/admin/brokers/{id}/fee-schedule,
// SM-837) and one account's resolution. broker-service owns every rule —
// ownership (BROKER_FEE_OWNER_MISMATCH), immutability
// (BROKER_FEE_PLAN_IMMUTABLE), rates (BROKER_FEE_RATE_INVALID), the GOLD
// redemption ceiling (BROKER_FEE_CEILING_EXCEEDED, or a coded 503
// BROKER_STATE_UNAVAILABLE when market-service cannot answer), the
// root's FEE_CHANGE status — and the resolution walk itself: which plan a
// node pays is read from the schedule, never computed here.
//
// There is no Idempotency-Key on these writes: a retry after a lost
// response creates a second plan or version, so the dialogs lock after
// an unknown outcome (feeOutcomeUnknown in components/brokers/fees.ts).

const feePlansPath = (rootID: string) => `${brokerPath(rootID)}/fee-plans`;

export const fetchFeePlans = (rootID: string, f: BrokerPageFilter) =>
  api<FeePlanListResponse>(`${feePlansPath(rootID)}${qs({ limit: f.limit, offset: f.offset })}`).then<FeePlanListResponse>(
    (r) => pageOf(r, f),
  );

/** One plan with every version (all currencies). */
export const fetchFeePlan = (rootID: string, planID: string) =>
  api<FeePlanItem>(`${feePlansPath(rootID)}/${encodeURIComponent(planID)}`).then<FeePlanItem>((r) => ({
    ...r,
    versions: r?.versions ?? [],
  }));

/** The root's assignment history, newest first. */
export const fetchFeeAssignments = (rootID: string, f: BrokerPageFilter) =>
  api<FeeAssignmentListResponse>(
    `${brokerPath(rootID)}/fee-assignments${qs({ limit: f.limit, offset: f.offset })}`,
  ).then<FeeAssignmentListResponse>((r) => pageOf(r, f));

/** What a node's users pay in one currency, as broker-service resolves it now. */
export const fetchFeeSchedule = (id: string, currency: string) =>
  api<StaffFeeScheduleResponse>(`${brokerPath(id)}/fee-schedule${qs({ currency })}`).then<StaffFeeScheduleResponse>(
    (r) => ({ ...r, upcoming: r?.upcoming ?? [], upcoming_assignments: r?.upcoming_assignments ?? [] }),
  );

/** One broker account's resolved fee component; atUnixMS 0 = now. */
export const fetchFeeResolution = (accountID: string, currency: string, atUnixMS: number) =>
  api<FeeResolution>(
    `/admin/broker-accounts/${encodeURIComponent(accountID)}/fee-resolution${qs({
      currency,
      at_unix_ms: atUnixMS > 0 ? atUnixMS : undefined,
    })}`,
  );

export const createFeePlan = (rootID: string, body: CreateFeePlanRequest) =>
  audited<FeePlanItem>(feePlansPath(rootID), body);

export const publishFeePlanVersion = (rootID: string, planID: string, body: PublishFeePlanVersionRequest) =>
  audited<FeePlanVersionItem>(`${feePlansPath(rootID)}/${encodeURIComponent(planID)}/versions`, body);

export const retireFeePlan = (rootID: string, planID: string, body: RetireFeePlanRequest) =>
  audited<FeePlanItem>(`${feePlansPath(rootID)}/${encodeURIComponent(planID)}/retire`, body);

export const publishFeeAssignment = (rootID: string, body: PublishFeeAssignmentRequest) =>
  audited<FeeAssignmentItem>(`${brokerPath(rootID)}/fee-assignments`, body);

// --- broker revenue-share plans and assignments (SM-841) ---
//
// /admin/brokers/{root_id}/revenue-share-plans* and
// /revenue-share-assignments (backoffice proxies them: reads
// BROKER_REVENUE_VIEW, writes BROKER_REVENUE_SHARE_MANAGE). broker-service
// owns every rule (BROKER_REVENUE_SHARE_INVALID / _CONFLICT with
// details.rule) and the resolution walk; the split that applies to a node
// is read from its fee schedule (revenue_share_plan_version_id,
// node_share_bps). Same no-Idempotency-Key caveat as the fee writes.

const sharePlansPath = (rootID: string) => `${brokerPath(rootID)}/revenue-share-plans`;

export const fetchRevenueSharePlans = (rootID: string, f: BrokerPageFilter) =>
  api<RevenueSharePlanListResponse>(`${sharePlansPath(rootID)}${qs({ limit: f.limit, offset: f.offset })}`).then<
    RevenueSharePlanListResponse
  >((r) => pageOf(r, f));

/** One plan with every version, newest first. */
export const fetchRevenueSharePlan = (rootID: string, planID: string) =>
  api<RevenueSharePlanDetail>(`${sharePlansPath(rootID)}/${encodeURIComponent(planID)}`).then<RevenueSharePlanDetail>(
    (r) => ({ ...r, versions: (r?.versions ?? []).map((v) => ({ ...v, shares: v.shares ?? [] })) }),
  );

/** The root's share assignment history, newest first. */
export const fetchRevenueShareAssignments = (rootID: string, f: BrokerPageFilter) =>
  api<RevenueShareAssignmentListResponse>(
    `${brokerPath(rootID)}/revenue-share-assignments${qs({ limit: f.limit, offset: f.offset })}`,
  ).then<RevenueShareAssignmentListResponse>((r) => pageOf(r, f));

export const createRevenueSharePlan = (rootID: string, body: CreateRevenueSharePlanRequest) =>
  audited<RevenueSharePlanItem>(sharePlansPath(rootID), body);

export const publishRevenueShareVersion = (rootID: string, planID: string, body: PublishRevenueShareVersionRequest) =>
  audited<RevenueSharePlanVersionItem>(`${sharePlansPath(rootID)}/${encodeURIComponent(planID)}/versions`, body);

export const retireRevenueSharePlan = (rootID: string, planID: string, body: RetireRevenueSharePlanRequest) =>
  audited<RevenueSharePlanItem>(`${sharePlansPath(rootID)}/${encodeURIComponent(planID)}/retire`, body);

export const publishRevenueShareAssignment = (rootID: string, body: PublishRevenueShareAssignmentRequest) =>
  audited<RevenueShareAssignmentItem>(`${brokerPath(rootID)}/revenue-share-assignments`, body);
